/**
 * **会话**：房间控制器 + 音量的落点。不 import Pixi。
 *
 * ## 为什么要有"控制器"这一层
 *
 * 纯模型（`room.ts`）和界面之间隔着一层可变状态。界面不该自己 `state = kick(...)`，
 * 因为那样"操作失败了要显示原因"就会散落在每个按钮的回调里。
 * 这一层把三件事收在一起：
 *
 * 1. 持有当前 `LobbyState`
 * 2. 每个操作都走**纯函数**，把 `{ok:false, reason}` 记成 `lastError`
 * 3. 变了就通知订阅者（界面重建）
 *
 * ## 网络在哪
 *
 * 这个 `createRoomController()` 是**离线 fallback**：它只操作本机的一份状态。
 * 真实联机由组合根（`main.ts`）把它接到 `src/host/online.ts` 的 `adopt()` 上 ——
 * 网络消息落成同一份 `LobbyState`，界面一行都不用改。
 */

import type { CardRegistry } from '../core/pack';
import type { PackRef } from './room';
import { t } from '../i18n';
import type { PlayerCount } from '../core/config';
import { roomConfigDiff, type RoomPresetStore } from './roomPreset';
import { IDLE_CONNECT, type ConnectState } from './connect';
import {
  canStart,
  createLobby,
  deckTotal,
  electGameHost,
  isSelfCreator,
  joinLobby,
  addBot as addLobbyBot,
  kickPlayer,
  patchConfig,
  resetDeck,
  setDeckCount,
  setPlayerPing,
  setPlayerReady,
  type ConfigPatch,
  type LobbyState,
  type StartCheck,
} from './room';

export type RoomListener = (state: LobbyState | null) => void;

export interface CreateRoomParams {
  readonly code: string;
  readonly selfId: string;
  readonly nickname: string;
  readonly playerCount?: PlayerCount;
}

export interface JoinRoomParams {
  readonly code: string;
  readonly selfId: string;
  readonly nickname: string;
}

export interface RoomController {
  get(): LobbyState | null;
  /** 最近一次操作失败的原因。UI 直接显示它 */
  lastError(): string | null;
  clearError(): void;
  /** 外部（联机层）判定失败：记下原因，UI 直接显示 */
  fail(reason: string): void;

  /**
   * **开始建房 / 开始进房。** 两条路都**不是同步**的 —— 它们先进入
   * "正在连接"，连上之后才轮到 `get()` 有值。
   *
   * 见 `ui/connect.ts`：调用方（汇合页）点完这两个之一就该推"正在连接"页，
   * 而房间页只在 `connect().phase === 'ready'` 之后才进得去。
   */
  createRoom(params: CreateRoomParams): void;
  joinRoom(params: JoinRoomParams): void;
  leaveRoom(): void;

  /**
   * **连接走到哪一步了。** 界面只读，不判。
   *
   * `connect.ts` 里是一个纯函数状态机（`reduceConnect`），这里只是持有它
   * 当前那一份快照。没接网络（单测、离线调试）时的实现是"永远是 idle"——
   * 那种场合没有"连接"这回事。
   */
  connect(): ConnectState;
  /** 连接状态变了。返回取消订阅 */
  subscribeConnect(listener: (state: ConnectState) => void): () => void;
  /**
   * **放弃这次连接**：收掉半连着的网络、把连接状态打回 `idle`。
   *
   * ⚠️ 连上之后（`ready`）**不要**调它 —— 那会把刚建好的那一桌收掉。
   * 正在连接页上的"取消/返回"和"失败自动退回"走它。
   */
  cancelConnect(): void;

  /**
   * **认下一份从远端来（或从网络层来）的大厅状态。**
   *
   * ⚠️ 它**绕过所有权限判定** —— 权限在模型层（`patchConfig` / `kickPlayer`），
   * 而那些判定看的是"本机是不是创建者"。远端来的东西已经由协议的发送方
   * 校验过了（`src/host/lobby.ts` 只认创建者发的游戏规则），所以到这里是可信的。
   */
  adopt(next: LobbyState): void;

  /** 改游戏规则（只有创建者能成） */
  patch(patch: ConfigPatch): void;
  setDeckCount(cardId: string, count: number): void;
  resetDeck(): void;
  /** 踢人（只有创建者能成） */
  kick(playerId: string): void;
  /** 加一个机器人席位（只有创建者能成；在线时还要广播） */
  addBot(): void;
  setReady(ready: boolean): void;
  setPing(playerId: string, pingMs: number | null): void;
  /** 记下导入了哪个存档。⚠️ 存档格式还没定，所以这里只存标签 */
  setImportedSave(label: string | null): void;

  /**
   * **房间配置的持久化。**
   *
   * presetDiff() 是当前配置相对官方默认的差集（只含改过的字段）。
   * 房间页离开时 savePreset()、下次进来 loadPreset() 再 patch() 恢复。
   */
  presetDiff(): ConfigPatch | null;
  savePreset(): void;
  loadPreset(): ConfigPatch | null;

  /**
   * **只给开发用的**：往房间里塞一个假玩家。
   *
   * ⚠️ 存在的理由：没有传输层就凑不出第二个人，而**踢人、准备、
   * "对局房主"徽章、开局前置条件**全都要有两个以上的人才看得出对不对。
   * 不塞假人，这一屏最要紧的那几条逻辑就只能靠肉眼读代码。
   *
   * 调用点被 `import.meta.env.DEV` 挡住，生产构建里不会被调到。
   * 联机落地后把这个方法连同那个按钮一起删掉。
   */
  addTestPlayer(id: string, nickname: string, pingMs: number): void;

  /** 本机是不是创建者 */
  isCreator(): boolean;
  canStart(): StartCheck;
  /** 对局房主（网络最快者，**和创建者无关**） */
  gameHost(): string | null;
  deckTotal(): number;

  subscribe(listener: RoomListener): () => void;
}

export function createRoomController(
  registry: CardRegistry,
  localPacks: readonly PackRef[] = [],
  presetStore: RoomPresetStore | null = null,
): RoomController {
  let state: LobbyState | null = null;
  let error: string | null = null;
  const listeners = new Set<RoomListener>();
  /**
   * 连接状态。
   *
   * ⚠️ 这一份控制器**不认识网络**（它是纯大厅模型，所以能单测）。
   * 真实的那一份由组合根（`main.ts`）覆写 —— 它那边才有 trystero 和计时器。
   * 这里的实现是"没有一个连接在走"，于是从没接过网络的场合
   * （单测、调试页）行为不变。
   */
  let connectState: ConnectState = IDLE_CONNECT;
  const connectListeners = new Set<(next: ConnectState) => void>();

  const publish = (): void => {
    for (const listener of listeners) listener(state);
  };

  /** 跑一个纯函数操作，成功就换状态，失败就记原因 */
  const apply = (result: { ok: true; value: LobbyState } | { ok: false; reason: string }): void => {
    if (result.ok) {
      error = null;
      state = result.value;
    } else {
      error = result.reason;
    }
    publish();
  };

  const selfId = (): string | null => state?.selfId ?? null;

  return {
    get: () => state,
    lastError: () => error,
    clearError() {
      if (error === null) return;
      error = null;
      publish();
    },
    fail(reason) {
      error = reason;
      publish();
    },

    adopt(next) {
      error = null;
      state = next;
      publish();
    },

    createRoom(params) {
      error = null;
      state = createLobby({
        code: params.code,
        selfId: params.selfId,
        nickname: params.nickname,
        registry,
        packs: localPacks,
        ...(params.playerCount === undefined ? {} : { playerCount: params.playerCount }),
      });
      publish();
    },

    joinRoom(params) {
      // ⚠️ 这是离线 fallback 的"加入"：本机建一份以自己为非创建者的房间状态。
      //    真实联机的名册由房主经 `host/lobby.ts` 下发，走的是 `adopt()`。
      //    这里不伪造任何"其他玩家"，房间页会明说此刻只有本机。
      error = null;
      const base = createLobby({
        code: params.code,
        selfId: params.selfId,
        nickname: params.nickname,
        registry,
        packs: localPacks,
      });
      // 把自己降成非创建者：创建者席位此刻还不存在（还没连上房主）
      state = {
        ...base,
        players: base.players.map((player) => ({ ...player, isCreator: false, ready: false })),
      };
      publish();
    },

    leaveRoom() {
      error = null;
      state = null;
      publish();
    },

    patch(patch) {
      if (state === null) return;
      apply(patchConfig(state, patch));
    },
    setDeckCount(cardId, count) {
      if (state === null) return;
      apply(setDeckCount(state, cardId, count));
    },
    resetDeck() {
      if (state === null) return;
      apply(resetDeck(state, registry));
    },
    kick(playerId) {
      if (state === null) return;
      apply(kickPlayer(state, playerId));
    },
    addBot() {
      if (state === null) return;
      apply(addLobbyBot(state, registry));
    },
    setReady(ready) {
      if (state === null) return;
      const id = selfId();
      if (id === null) return;
      error = null;
      state = setPlayerReady(state, id, ready);
      publish();
    },
    setPing(playerId, pingMs) {
      if (state === null) return;
      const next = setPlayerPing(state, playerId, pingMs);
      if (next === state) return;
      state = next;
      publish();
    },
    setImportedSave(label) {
      if (state === null) return;
      error = null;
      state = { ...state, importedSave: label };
      publish();
    },

    presetDiff: () => (state === null ? null : roomConfigDiff(state.config, registry)),
    savePreset() {
      if (state === null) return;
      presetStore?.save(roomConfigDiff(state.config, registry));
    },
    loadPreset: () => presetStore?.load() ?? null,

    addTestPlayer(id, nickname, pingMs) {
      if (state === null) return;
      error = null;
      state = setPlayerPing(joinLobby(state, { id, nickname }), id, pingMs);
      publish();
    },

    connect: () => connectState,
    subscribeConnect(listener) {
      connectListeners.add(listener);
      return () => connectListeners.delete(listener);
    },
    cancelConnect() {
      connectState = IDLE_CONNECT;
      state = null;
      for (const listener of connectListeners) listener(connectState);
      publish();
    },

    isCreator: () => (state === null ? false : isSelfCreator(state)),
    canStart: () => (state === null ? { ok: false, problems: [t('session.notInRoom')] } : canStart(state, registry)),
    gameHost: () => (state === null ? null : electGameHost(state.players)),
    deckTotal: () => (state === null ? 0 : deckTotal(state)),

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

// ============================================================
// 音量
// ============================================================

/**
 * 音量设置真正落下去的地方。
 *
 * 界面**不直接碰音频引擎** —— 它只喊"BGM 音量变成 0.4 了"，谁来听是这里的事。
 * 这样设置页可以在 BGM 还没启动、甚至 `audio/` 模块整个不存在的情况下照常工作。
 */
export interface AudioSink {
  /** 0..1 */
  setBgmVolume(volume: number): void;
  /** 0..1 */
  setSfxVolume(volume: number): void;
  /**
   * **BGM 的"播放故障"滤镜 + 混响剂量**（0..1）。
   *
   * 评审：自 IMPACT 时刻出现且达到最大，随后衰减消失 —— 剂量由
   * `render/impact.ts` 的 `fxEnvelope` 按帧算，这里只负责转给音频那一侧。
   *
   * ⚠️ 它**不动音量**：上一版那记"留白"（把 BGM 掐掉再抬回来）让无缝循环的
   *    BGM 听上去像硬切，评审让整个去掉了。这一路只改音色。
   */
  setBgmFx(amount: number): void;
  /**
   * **BGM 的音量增益倍数**（1 = 不动，> 1 = 正在渐强）。
   *
   * 评审：「IMPACT 时要有逐渐上升的音量增益，从回合结束抽牌触发濒死那一刻开始
   * 上升，IMPACT 瞬间达到最大，然后快速衰减」。
   *
   * ⚠️ 它**乘**在设置音量上（`volume × boost`），而且**只加不减** ——
   *    第三轮删掉的"留白"是往下掐，那会把无缝循环切成硬切；渐强是往上推。
   */
  setBgmBoost(factor: number): void;
  /**
   * **放一记打击音效**（低频下潜 + 噪声 crack，合成，不用素材）。
   *
   * 只在**主打击**那一帧调一次。没有音频的环境（单测、静音、自动播放被拦）
   * 就当没这回事 —— 它和 BGM 一样"不响也不算错"。
   */
  playImpact(): void;
}
